import React, { useEffect, useState } from "react";
import "./Player.scss";

import { uuid, isAndroid } from "../../utils/util";
import { style } from "../../utils/image";
import URLFactory from "../../utils/url";
import { IMedium, ILiveSource } from "../../store/VideoStore";
import RootStore from "../../store/RootStore";
import VideoManager from "./Manager";
import PluginDanmuku from "./PluginDanmuku";
import PluginStat from "./PluginStat";
import PluginException from "./PluginException";
import PluginCustomProgress from "./PluginCustomProgress";
import PluginLoading from "./PluginLoading";
import PluginNotice from "./PluginNotice";
import PluginLivePlay from "./PluginLivePlay";
import PluginControlBar from "./PluginControlBar";
import PluginWaitMask from "./PluginWaitMask";
import PluginFullScreen from "./PluginFullScreen";
import PluginFilter from "./PluginFilter";
import PluginPlayHistory from './PluginPlayHistory';

import stat from "../../utils/stat";
import PluginMiniProgramDownload from "./PluginMiniProgramDownload";

export interface IVideoPlayer {
  autoPlay: boolean;
  source: IMedium;
  danmuku: boolean;
  rootStore?: RootStore;
  targetId?: string;
  onCountDown?: () => void;
  onException?: (isPlay: boolean) => void;
  onVideoTime?: (time: number) => void;
  onCreated?: (id: string) => void;
  width?: string | number;
  height?: string | number;
  controlBarVisibility?: string;
  useSnapShot?: boolean;
  videoWidth?: string | number;
  videoHeight?: string | number;
  preload?: boolean;
  componentDownload?: boolean;
  onRequestFullscreen?: (player:any) => void;
  onCancelFullscreen?: (player:any) => void;
}

export const videoLayout = (source: IMedium) => {
  return [
    { name: "bigPlayButton", align: "cc" },
    // { name: 'H5Loading', align: 'cc' },
    {
      name: "controlBar",
      align: "blabs",
      x: 0,
      y: 0,
      children: [
        { name: 'progress', align: 'blabs', x: 0, y: 44 },
        { name: "playButton", align: "tl", x: 15, y: 12 },
        { name: "timeDisplay", align: "tl", x: 10, y: 7 },
        { name: "fullScreenButton", align: "tr", x: 10, y: 12 },
        // {name:"subtitle", align:"tr",x:15, y:12},
        // { name: 'setting', align: 'tr', x: 10, y: 12 },
        // { name: "volume", align: "tr", x: 10, y: 10 },
      ],
    },
  ];
};

export const liveLayout = (source: IMedium) => {
  return [
    { name: "bigPlayButton", align: "cc" },
    {
      name: "controlBar",
      align: "blabs",
      x: 0,
      y: 0,
      children: [
        // { name: 'liveDisplay', align: 'tlabs', x: 15, y: 5 },
        // { name: "playButton", align: "tlabs", x: 15, y: 12 },
        { name: "fullScreenButton", align: "tr", x: 10, y: 12 },
        // // {name:"subtitle", align:"tr",x:15, y:12},
        // // {name:"setting", align:"tr",x:15, y:12},
        // { name: "volume", align: "tr", x: 5, y: 10 },
      ],
    },
  ].filter(Boolean);
};

export const protocolUrl = (hash: string, protocol: string = "") => {
  if (hash && hash.match(/^(http|rtmp)/gi)) {
    hash = hash.replace(/^(https|http|rtmp):/i, '');
    return (protocol || window.location.protocol) + hash;
  } else {
    return (
      (protocol || window.location.protocol) +
      "//image1" +
      window.__basename +
      "/" +
      hash
    );
  }
};

export const getVideoSourceUrl = (source: IMedium, protocol?:string) => {
  if (source && (source.type == "video" || source.type == "360" || source.type == 'segment')) {
    return protocolUrl(source.source, protocol);
  } else if (source && source.type == "live") {
    let source_detail = source.source_detail as ILiveSource;
    if (source_detail) {
      if (source_detail.status == "rollback") {
        return protocolUrl(String(source_detail.cut_play_url));
      }
      return protocolUrl(String(source_detail.play_url));
    }
  }

  return "";
};

const getVideoDuration = (source: IMedium) => {
  if (source && (source.type == "video" || source.type == "360")) {
    return [0, -1];
  } else if (source && source.type == "live") {
    let source_detail = source.source_detail as ILiveSource;
    if (source_detail) {
      if (source_detail.status == "rollback") {
        let { cut_play_url } = source_detail;
        let cutPlayCurl = URLFactory(cut_play_url);
        let start = cutPlayCurl.getParam("query_start");
        let end = cutPlayCurl.getParam("query_duration");
        if (start && end) {
          return [Number(start), Number(end)];
        } else {
          return [0, -1];
        }
      } else {
        return [];
      }
    }
  }

  return [];
};

export const getSourceLive = (source: IMedium) => {
  if (source && (source.type == "video" || source.type == "360")) {
    return false;
  } else if (source && source.type == "live") {
    let source_detail = source.source_detail as ILiveSource;
    if (source_detail) {
      if (source_detail.status == "rollback") {
        return false;
      } else {
        return true;
      }
    }
  }

  return false;
};

export default (props: IVideoPlayer) => {
  const {
    source,
    autoPlay,
    danmuku,
    rootStore,
    onCountDown,
    onException,
    onVideoTime,
    targetId,
    onCreated,
    componentDownload
  } = props;
  const [inited, setInited] = useState(false);
  const [playerId, setPlayerId] = useState("");

  const { wxuserStore } = rootStore!;

  // 初始化容器
  useEffect(() => {
    if (!playerId) {
      setPlayerId(targetId ? targetId : `player_${uuid()}`);
      // stat.event('video_play', source.id);
    }
  }, [!!playerId]);

  const shouldShowDownloadBtn = function (componentDownload?: boolean) {
    // 如果视频是直播视频，不管是不是回放了，都不展现，相当于只在短视频以及即刻秀视频的情况下，才提供下载功能
    if (source && source.type == "live") {
      return false;
    }

    // 360即刻秀是一直可以下载的
    if (source && source.type == "360") {
      return true;
    }

    // 如果全局关闭了视频下载功能，也是不允许下载的
    if (!window.configDetail.download_video) {
      return false;
    }

     // 如果组件主动关闭一下载，那么则不显示
    if(typeof componentDownload != 'undefined' && !componentDownload) {
      return false;
    }

    // 开发时一定展示，方便调试
    // 虽然说开发时会显示，但是因为在H5上并不会被渲染出样式，表现是占用了空间（毕竟有宽度）但是什么都没有
    // 目前版本的微信PC端不知道为啥放开后会报错，所以这里也不做渲染
    if (process.env.NODE_ENV === "development" && !window.isweixinweb) {
      return true;
    }

   

    return !wxuserStore.user.is_mock && window.isweixin && !window.isweixinweb;
  };

  const createPlayer = () => {
    let player = new window.Aliplayer(
      {
        id: playerId,
        width: props.width ? props.width : "100%",
        height: props.height ? props.height : "100%",
        videoWidth: props.videoWidth ? props.videoWidth : "100%",
        videoHeight: props.videoHeight ? props.videoHeight : "100%",
        useH5Prism: true,
        useFlashPrism: false,
        playsinline: true,
        showBuffer: true,
        preload: typeof props.preload != 'undefined' ? props.preload : false,
        rePlay: true,
        autoplay: false,
        // x5_type: 'h5',
        x5_video_position: "top",
        x5_fullscreen: true,
        // autoPlay: true,
        cover: source.thumb ? style(source.thumb, "wx_750_420") : (props.useSnapShot ? `${source.source}?x-oss-process=video/snapshot,t_3000,f_jpg,w_750,m_fast` : null),
        controlBarVisibility: props.controlBarVisibility || "always",
        // showBarTime: 1000,
        isLive: getSourceLive(source) ? true : false,
        // liveRetry:5,
        // @ts-ignore
        language: window.configDetail.locale.toLowerCase(),
        source: getVideoSourceUrl(source),
        skinLayout: getSourceLive(source)
          ? liveLayout(source)
          : videoLayout(source),
        components: [
          getSourceLive(source) && danmuku
            ? {
                name: "CustomDanmuSwitch",
                type: PluginDanmuku,
                args: [rootStore],
              }
            : null,
          {
            name: "Stat",
            type: PluginStat,
            args: [source.id, playerId],
          },
          !getSourceLive(source) ? {
            name: "Exception",
            type: PluginException,
            args: [source, onException],
          } : null,
          {
            name: "Loading",
            type: PluginLoading,
            args: [],
          },
          {
            name: "Notice",
            type: PluginNotice,
            args: [source, onCountDown],
          },
          props.controlBarVisibility != 'always' ? {
            name: "ControlBar",
            type: PluginControlBar,
            args: [0],
          } : null,
          {
            name: "FullscreenEvent",
            type: PluginFullScreen,
            args: [props.onRequestFullscreen, props.onCancelFullscreen],
          },
          source && source.source_detail
            ? {
                name: "Filter",
                type: PluginFilter,
                args: [source.source_detail.filter_option],
              }
            : null,
          source.source_detail &&
          ["complete", "directend", "stop"].indexOf(
            source.source_detail.status
          ) > -1
            ? {
                name: "WaitMask",
                type: PluginWaitMask,
                args: [source.source_detail.status],
              }
            : null,
          // 直播状态下，倍速按钮不会生效
          !getSourceLive(source)
            ? {
                name: "RateComponent",
                type: window.AliPlayerComponent.RateComponent,
              }
            : null,
          getSourceLive(source)
            ? {
                name: "LivePlayButton",
                type: PluginLivePlay,
                args: [],
              }
            : null,
          // 这里其实还要判断一下，是否为回放状态
          // getVideoDuration(source).length
          //   ? {
          //       name: "CustomProgress",
          //       type: PluginCustomProgress,
          //       args: getVideoDuration(source),
          //     }
          //   : null,
          // 已登录的微信手机端才展示
          shouldShowDownloadBtn(componentDownload)
            ? {
                name: "MiniProgramDownload",
                type: PluginMiniProgramDownload,
                args: [getVideoSourceUrl(source, 'https:'), source.id],
              }
            : null,
          // 非直播视频，需要记录时间点
          !getSourceLive(source) ? {
            name: "PlayHistory",
            type: PluginPlayHistory,
            args: [source.id]
          } : null,
        ].filter(Boolean),
      },
      function (instance: any) {
        VideoManager.setPlayerInstance(playerId, instance);
        setInited(true);
        // instance.getComponent('CustomDanmuSwitch').setDanmuku(danmuku);
        // playsInline={props.fullscreen ? false : true}
        // autoPlay={autoPlay || false}
        // // 这个能保证安卓上的行内播放
        // x5-playsinline="true"
        instance.setVolume(1);

        instance.tag.setAttribute("playsinline", "true");
        instance.tag.setAttribute("x5-playsinline", "true");

        instance.tag.style.objectFit = "contain";

        // instance.tag.style.width = 'auto';

        // android下的全屏，不能把video的宽高都设置为100%，否则会不居中显示，只需要其中一个值就好
        if (isAndroid()) {
          instance.tag.style.width = "auto";
        }
        if(props.videoWidth) {
          instance.tag.style.width = props.videoWidth;
        }
        if(props.videoHeight) {
          instance.tag.style.height = props.videoHeight;
        }

        if (onCreated) {
          onCreated(playerId);
        }
      }
    );
  };

  // 初始化播放器
  useEffect(() => {
    const disposePlayer = () => {
      let player = VideoManager.getPlayerInstance(playerId);
      if (player) {
        player.dispose();
      }
    };

    const resetPlayer = () => {
      disposePlayer();
      createPlayer();
    };

    let created = false;
    if (source && playerId) {
      created = true;
      resetPlayer();
    }

    window.addEventListener("custom-dispose-player", resetPlayer);

    return () => {
      window.removeEventListener("custom-dispose-player", resetPlayer);
      if (created) {
        disposePlayer();
        VideoManager.deleteKey(playerId);
      }
    };
  }, [!!source && !!playerId]);

  // 评论的显示逻辑
  useEffect(() => {
    let instance = null as any;
    let lastTime = -1;

    const onTimeUpdate = (e: any) => {
      if (instance) {
        let time = instance.getCurrentTime();
        time = Math.round(time);
        if (time != lastTime) {
          if (onVideoTime) {
            onVideoTime(time);
            lastTime = time;
          }
        }
      }
    };

    if (inited) {
      instance = VideoManager.getPlayerInstance(playerId);

      if (!getSourceLive(source)) {
        instance.on("timeupdate", onTimeUpdate);
      }
    }

    return () => {
      if (instance) {
        instance.off("timeupdate", onTimeUpdate);
      }
    };
  }, [inited]);

  let statusWait =
    source &&
    source.source_detail &&
    ["wait", "config"].indexOf(source.source_detail.status) > -1;

  return (
    <div className="ali-video-player">
      <div className="wrapper">
        <div className="video-container" id={playerId} />
      </div>
      {statusWait ? (
        <style>{`#${playerId} .prism-big-play-btn {opacity:0;}`}</style>
      ) : null}
    </div>
  );
};
